import * as React from 'react';
import {
  type DateRange,
  type Matcher,
  type PropsBase,
  type PropsRange,
} from 'react-day-picker';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {toCssSize} from '../../lib/utils';
import {isMobileModalModeAvailable} from '../../lib/runtimeSettings';
import {useIsMobile, useOutsideClick} from '../../hooks';
import {FieldErrorMessage} from '../../field-error-message';
import {Fieldset} from '../_fieldset';
import {createDisabledMatchers} from './createDisabledMatchers';
import {DEFAULT_DISPLAY_FORMAT, formatDate, parseDate, toDate} from './dateUtils';
import type {DateInput} from './dateUtils';
import {useRangeValue} from './hooks/useRangeValue';
import {useRangeTextInputs} from './hooks/useRangeTextInputs';
import {useRangeMonthSync} from './hooks/useRangeMonthSync';
import {resolveRangeSelection} from './utils/rangeSelection';
import {DateRangeInputs} from './components/DateRangeInputs';
import {DateRangeCalendar} from './components/DateRangeCalendar';
import {DateRangePopover} from './components/DateRangePopover';

type OpenDirection = 'up' | 'down';

type FocusedInput = 'from' | 'to' | null;

type DateRangePickerProps = Omit<Partial<PropsRange>, 'mode' | 'selected'> &
  Omit<PropsBase, 'mode' | 'selected' | 'disabled'> & {
    label?: string;
    value?: DateRange;
    defaultValue?: DateRange;
    onChange?: (value: DateRange | undefined, name?: string) => void;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    onBlur?: (value: DateRange | undefined) => void;
    name?: string;
    invalid?: boolean;
    error?: string;
    disabled?: boolean;
    readOnly?: boolean;
    loading?: boolean;
    optional?: boolean | string;
    tooltip?: React.ReactNode;
    className?: string;
    displayFormat?: string;
    placeholder?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    minDate?: DateInput;
    maxDate?: DateInput;
    minDays?: number;
    maxDays?: number;
    disabledDays?: Matcher | Matcher[];
    hideClearDates?: boolean;
    hideCalendarIcon?: boolean;
    hideErrorMessage?: boolean;
    helperText?: string;
    width?: number | string;
    numberOfMonths?: number;
    openDirection?: OpenDirection;
    autoFocusOnOpen?: boolean;
  };

export type DateRangePickerImperativeProps = {
  setDateRange: (dateRange?: DateRange) => void;
  focus: () => void;
  open: () => void;
  close: () => void;
};

const DateRangePicker = React.forwardRef<
  DateRangePickerImperativeProps,
  DateRangePickerProps
>(function DateRangePicker(
  {
    label,
    value: externalValue,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    name,
    invalid,
    error,
    disabled,
    readOnly,
    loading,
    optional,
    tooltip,
    className,
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    fromPlaceholder,
    toPlaceholder,
    minDate,
    maxDate,
    minDays,
    maxDays,
    disabledDays,
    hideClearDates,
    hideCalendarIcon,
    hideErrorMessage,
    helperText,
    width,
    numberOfMonths,
    openDirection = 'down',
    autoFocusOnOpen = true,
    components: customComponents,
    ...dayPickerProps
  },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fromInputRef = React.useRef<HTMLInputElement>(null);
  const toInputRef = React.useRef<HTMLInputElement>(null);
  const reactId = React.useId();
  const baseId = name ?? `dash-daterange-${reactId}`;
  const fromId = `${baseId}-from`;
  const toId = `${baseId}-to`;
  const labelId = `${baseId}-label`;
  const errorId = `${baseId}-error`;

  const normalizedValue = React.useMemo<DateRange | undefined>(() => {
    if (externalValue === undefined) return undefined;
    return {from: toDate(externalValue?.from), to: toDate(externalValue?.to)};
  }, [externalValue]);
  const normalizedDefaultValue = React.useMemo<DateRange | undefined>(() => {
    if (defaultValue === undefined) return undefined;
    return {from: toDate(defaultValue?.from), to: toDate(defaultValue?.to)};
  }, [defaultValue]);

  const {value, commit} = useRangeValue({
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    onChange,
    name,
  });

  const normalizedMinDate = React.useMemo(() => toDate(minDate), [minDate]);
  const normalizedMaxDate = React.useMemo(() => toDate(maxDate), [maxDate]);

  const formatter = React.useMemo(() => formatDate(displayFormat), [displayFormat]);
  const parser = React.useMemo(() => parseDate(displayFormat), [displayFormat]);

  const closeCalendarRef = React.useRef<() => void>(() => {});

  const handleFromComplete = React.useCallback(() => {
    toInputRef.current?.focus();
  }, []);

  const handleToComplete = React.useCallback(() => {
    toInputRef.current?.blur();
    closeCalendarRef.current();
  }, []);

  const {
    fromText,
    toText,
    setFromText,
    setToText,
    handleFromChange,
    handleToChange,
    handleFromBlur,
    handleToBlur,
  } = useRangeTextInputs({
    value,
    format: formatter,
    parse: parser,
    displayFormat,
    onCommit: commit,
    onBlur,
    onFromComplete: handleFromComplete,
    onToComplete: handleToComplete,
  });

  const {month, setMonth, syncMonthToValue} = useRangeMonthSync(value);

  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedInput, setFocusedInput] = React.useState<FocusedInput>(null);
  const [autoFocus, setAutoFocus] = React.useState(false);
  const isMobile = useIsMobile() && isMobileModalModeAvailable();
  const {t} = useTranslation();

  const drawerTitle = label ?? t('select_dates');
  const drawerDescription = label ?? t('pick_date_range');
  const isEmpty = !value?.from && !value?.to;
  const isBlocked = Boolean(disabled) || Boolean(loading);
  const isInvalid = Boolean(invalid || error);
  const isFocused = focusedInput !== null || isOpen;
  const wrapperWidth = toCssSize(width);
  const monthsToShow = numberOfMonths ?? (isMobile ? 1 : 2);

  const closeCalendar = React.useCallback(() => {
    setIsOpen(false);
    setFocusedInput(null);
    setAutoFocus(false);
    if (value?.from) setMonth(value.from);
  }, [setMonth, value?.from]);
  closeCalendarRef.current = closeCalendar;

  const openCalendar = React.useCallback(() => {
    if (isBlocked || readOnly) return;
    setIsOpen(true);
  }, [isBlocked, readOnly]);

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: closeCalendar,
    isDisabled: !isOpen || isMobile,
  });

  const handlePickerChange: NonNullable<PropsRange['onSelect']> = React.useCallback(
    (range, pickedDate) => {
      const {next, shouldClose} = resolveRangeSelection({
        previous: value,
        range,
        pickedDate,
      });
      syncMonthToValue(next);
      commit(next);
      if (shouldClose) closeCalendar();
    },
    [closeCalendar, commit, syncMonthToValue, value],
  );

  const handleReset = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    commit(undefined);
    setFromText('');
    setToText('');
    setMonth(new Date());
  };

  const disabledMatchers = React.useMemo(
    () =>
      createDisabledMatchers({
        minDate: normalizedMinDate,
        maxDate: normalizedMaxDate,
        maxDays,
        minDays,
        datesRange: value,
        disabledDays,
      }),
    [disabledDays, maxDays, minDays, normalizedMaxDate, normalizedMinDate, value],
  );

  const toggleCalendar = () => {
    if (isOpen) {
      closeCalendar();
      return;
    }
    openCalendar();
    if (autoFocusOnOpen) setAutoFocus(true);
  };

  React.useImperativeHandle(
    ref,
    () => ({
      setDateRange: range => {
        syncMonthToValue(range);
        commit(range);
        setFromText(range?.from ? formatter(range.from) : '');
        setToText(range?.to ? formatter(range.to) : '');
      },
      focus: () => fromInputRef.current?.focus(),
      open: openCalendar,
      close: closeCalendar,
    }),
    [
      closeCalendar,
      commit,
      formatter,
      openCalendar,
      setFromText,
      setToText,
      syncMonthToValue,
    ],
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)]',
        disabled && 'cursor-not-allowed opacity-50',
        loading && 'cursor-progress opacity-50',
        className,
      )}
      style={wrapperWidth ? {width: wrapperWidth} : undefined}
    >
      <div className="relative min-h-[var(--field-min-height,48px)] w-full">
        <div className="relative w-full">
          <DateRangeInputs
            fromId={fromId}
            toId={toId}
            name={name}
            labelId={labelId}
            fromText={fromText}
            toText={toText}
            fromPlaceholder={fromPlaceholder}
            toPlaceholder={toPlaceholder}
            fromInputRef={fromInputRef}
            toInputRef={toInputRef}
            isEmpty={isEmpty}
            isFocused={isFocused}
            isBlocked={isBlocked}
            isInvalid={isInvalid}
            loading={loading}
            readOnly={readOnly}
            hideClearDates={hideClearDates}
            hideCalendarIcon={hideCalendarIcon}
            isOpen={isOpen}
            focusedInput={focusedInput}
            clearLabel={t('clear_dates')}
            openCalendarLabel={t('open_calendar')}
            onFromTextChange={handleFromChange}
            onToTextChange={handleToChange}
            onFromFocus={event => {
              onFocus?.(event);
              setFocusedInput('from');
              if (!readOnly && !isBlocked) setIsOpen(true);
            }}
            onToFocus={event => {
              onFocus?.(event);
              setFocusedInput('to');
              if (!readOnly && !isBlocked) setIsOpen(true);
            }}
            onFromBlur={() => {
              setFocusedInput(prev => (prev === 'from' ? null : prev));
              handleFromBlur();
            }}
            onToBlur={() => {
              setFocusedInput(prev => (prev === 'to' ? null : prev));
              handleToBlur();
            }}
            onRowClick={() => {
              if (isBlocked || readOnly) return;
              if (!focusedInput) fromInputRef.current?.focus();
            }}
            onReset={handleReset}
            onToggleCalendar={toggleCalendar}
          />
          <Fieldset
            isFocused={isFocused}
            invalid={isInvalid}
            isEmpty={isEmpty}
            isActivated={!isEmpty || isFocused}
            disabled={disabled}
            loading={loading}
            readOnly={readOnly}
            htmlFor={fromId}
            labelId={labelId}
            legend={label}
            label={label}
            tooltip={tooltip}
          />
          <DateRangePopover
            isOpen={isOpen && !isMobile}
            isMobile={false}
            openDirection={openDirection}
            drawerTitle={drawerTitle}
            drawerDescription={drawerDescription}
            onClose={closeCalendar}
          >
            <DateRangeCalendar
              value={value}
              month={month}
              onMonthChange={setMonth}
              onSelect={handlePickerChange}
              numberOfMonths={monthsToShow}
              minDate={normalizedMinDate}
              maxDate={normalizedMaxDate}
              maxDays={maxDays}
              minDays={minDays}
              autoFocus={autoFocus}
              disabledMatchers={disabledMatchers}
              components={customComponents}
              {...dayPickerProps}
            />
          </DateRangePopover>
        </div>
        <DateRangePopover
          isOpen={isOpen && isMobile}
          isMobile={true}
          openDirection={openDirection}
          drawerTitle={drawerTitle}
          drawerDescription={drawerDescription}
          onClose={closeCalendar}
        >
          <DateRangeCalendar
            value={value}
            month={month}
            onMonthChange={setMonth}
            onSelect={handlePickerChange}
            numberOfMonths={monthsToShow}
            minDate={normalizedMinDate}
            maxDate={normalizedMaxDate}
            maxDays={maxDays}
            minDays={minDays}
            autoFocus={autoFocus}
            disabledMatchers={disabledMatchers}
            components={customComponents}
            {...dayPickerProps}
          />
        </DateRangePopover>
        {!error && optional && (
          <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {!error && helperText && (
          <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
            {helperText}
          </span>
        )}
        {error && !hideErrorMessage && (
          <FieldErrorMessage
            id={errorId}
            message={error}
            className="mt-[1px] text-[14px]"
          />
        )}
      </div>
    </div>
  );
});

export {DateRangePicker};
export type {DateRangePickerProps, OpenDirection};
